export interface ScannedProduct {
  title: string
  editionLabel: string
  priceNumber: number
  priceText: string
  thumbnailUrl: string
}

const FALLBACK_PRODUCTS: ScannedProduct[] = [
  {
    title: 'Combo Completo',
    editionLabel: 'Receta + Estrategia de Ventas',
    priceNumber: 16.97,
    priceText: '$16.97',
    thumbnailUrl:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/wxBGsmrT3DBz6uBHHqrUUB/COMBO_1.png',
  },
  {
    title: 'Método Milhojas Perfecta',
    editionLabel: 'Receta Digital',
    priceNumber: 9.97,
    priceText: '$9.97',
    thumbnailUrl:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/2RTNkXmNh3tX5Jpr67nfKE/m3.png',
  },
]

function tryJsonLd(): ScannedProduct[] {
  const products: ScannedProduct[] = []
  try {
    const scripts = document.querySelectorAll(
      'script[type="application/ld+json"]',
    )
    scripts.forEach((script) => {
      const data = JSON.parse(script.textContent || '{}')
      const items = Array.isArray(data) ? data : [data]
      items.forEach((item: any) => {
        if (item['@type'] === 'Product' && item.offers) {
          const offer = Array.isArray(item.offers)
            ? item.offers[0]
            : item.offers
          products.push({
            title: item.name || 'Product',
            editionLabel: item.description || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${parseFloat(offer.price || 0).toFixed(2)}`,
            thumbnailUrl:
              (Array.isArray(item.image) ? item.image[0] : item.image) || '',
          })
        }
      })
    })
  } catch {
    // JSON-LD parsing failed, return empty
  }
  return products
}

function scanDom(): ScannedProduct[] {
  const products: ScannedProduct[] = []
  const priceRegex = /\$\s?(\d+(?:\.\d{1,2})?)/g
  const pricingSection = document.getElementById('comprar')
  if (!pricingSection) return products

  // Find all elements that contain price-like text
  const allElements = pricingSection.querySelectorAll('*')
  const priceElements: { el: Element; price: number; priceText: string }[] = []

  allElements.forEach((el) => {
    // Only check direct text content (not children) to avoid duplicates
    const directText = Array.from(el.childNodes)
      .filter((n) => n.nodeType === Node.TEXT_NODE)
      .map((n) => n.textContent || '')
      .join('')

    const fullText = el.textContent || ''
    const match = priceRegex.exec(fullText)
    priceRegex.lastIndex = 0

    if (match && directText.includes('$')) {
      const price = parseFloat(match[1])
      if (price > 5 && price < 100) {
        priceElements.push({ el, price, priceText: `$${price.toFixed(2)}` })
      }
    }
  })

  // For each price element found, look for a nearby title and image
  const seen = new Set<number>()
  priceElements.forEach(({ el, price, priceText }) => {
    if (seen.has(price)) return

    // Walk up to find a container card/section
    let container: Element | null = el
    for (let i = 0; i < 8; i++) {
      if (container?.parentElement) container = container.parentElement
      if (container?.id === 'comprar') break
    }
    if (!container) return

    // Find nearby heading or bold text for title
    const heading =
      container.querySelector('h2, h3, h4') ||
      container.querySelector('.font-bold, .font-semibold')
    const title = heading?.textContent?.trim() || ''

    // Find nearby image for thumbnail
    const img = container.querySelector('img')
    const thumbnailUrl = img?.getAttribute('src') || ''

    if (title && thumbnailUrl && price > 0) {
      seen.add(price)
      products.push({
        title: title.substring(0, 60),
        editionLabel: '',
        priceNumber: price,
        priceText,
        thumbnailUrl,
      })
    }
  })

  return products
}

export function scanProducts(): ScannedProduct[] {
  // 1. Try JSON-LD structured data
  const jsonLdProducts = tryJsonLd()
  if (jsonLdProducts.length > 0) return jsonLdProducts

  // 2. Try DOM scanning
  const domProducts = scanDom()
  if (domProducts.length > 0) return domProducts

  // 3. Fall back to hardcoded products
  return FALLBACK_PRODUCTS
}

/** Pick a random product, with 70% weight on the first (combo) if using fallbacks */
export function pickRandomProduct(products: ScannedProduct[]): ScannedProduct {
  if (products.length === 0) return FALLBACK_PRODUCTS[0]
  if (products.length === 1) return products[0]

  // If these are the fallback products (or any 2-product list), weight 70/30
  if (products.length === 2) {
    return Math.random() < 0.7 ? products[0] : products[1]
  }

  // For 3+ products, uniform random
  return products[Math.floor(Math.random() * products.length)]
}
